import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/ic-exit-rounded-purple.svg';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import { Button } from '../UI-CHAKRA/Button';
import { Checkbox } from '../UI-CHAKRA/Checkbox';

interface PspConsentStepProps {
  programName: string;
  optoutUrl: string;
  partnerProgramUrl: string;
  patientConsent: boolean;
  professionalConsent: boolean;
  patientConsentOptional?: boolean;
  doctorConsentOptional?: boolean;
  onPatientConsentChange: (checked: boolean) => void;
  onProfessionalConsentChange: (checked: boolean) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const PspConsentStep: React.FC<PspConsentStepProps> = ({
  programName,
  optoutUrl,
  partnerProgramUrl,
  patientConsent,
  professionalConsent,
  patientConsentOptional = false,
  doctorConsentOptional = false,
  onPatientConsentChange,
  onProfessionalConsentChange,
  onConfirm,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isConfirmDisabled =
    (!patientConsentOptional && !patientConsent) || (!doctorConsentOptional && !professionalConsent);

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
        {isMobile ? (
          <>
            <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
              <LeftArrowIcon onClick={onClose} cursor={'pointer'} />
              <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
                Voltar
              </Text>
            </Flex>
            <Flex flexDirection={'column'} padding={'0px 20px'} mb={'16px'}>
              <Text fontSize={'18px'} fontWeight={700} color={'#343A40'}>
                Programa de Suporte ao Paciente
              </Text>
            </Flex>
          </>
        ) : (
          <Flex flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} padding={'20px'}>
            <Text fontSize={'18px'} fontWeight={700} color={'#343A40'}>
              Programa de Suporte ao Paciente
            </Text>
            <CloseIcon onClick={onClose} cursor={'pointer'} />
          </Flex>
        )}
      </Box>

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'20px'}>
        <Flex flexDirection={'column'} gap={'1px'}>
          <Text fontSize={'18px'} fontWeight={700} color={'primary.main'}>
            Confirmação de consentimento
          </Text>

          <Text fontSize={'14px'} color={'#343A40'} lineHeight={'17px'} paddingBottom={'18px'}>
            Para continuar você precisa coletar o consentimento do seu paciente para pré-cadastro no Programa:{' '}
          </Text>

          <Flex flexDirection={'column'} gap={'16px'}>
            <Checkbox
              isChecked={patientConsent}
              onChange={(e) => onPatientConsentChange(e.target.checked)}
              size="md"
              alignItems={'flex-start'}
            >
              <Box ml={'8px'} flex={1}>
                <Text fontSize={'14px'} color={'#343A40'} lineHeight={'20px'}>
                  Confirmo que obtive o consentimento do paciente para o seu pré-cadastro no Programa de Suporte ao
                  Paciente{' '}
                  <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                    {programName || '[Nome do Programa]'}
                  </Text>
                  , tendo-o informado de que:
                  {patientConsentOptional && (
                    <Text as="span" color={'#6C757D'}>
                      {' '}(opcional)
                    </Text>
                  )}
                </Text>
                <Box as="ul" pl={'20px'} mt={'8px'} mb={0}>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                    I. Seus dados de nome, telefone e email serão compartilhados com os responsáveis pelo Programa para
                    o contato e prosseguimento do cadastro;
                  </Text>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                    II. Poderá receber comunicações da Mevo relacionadas à sua jornada de tratamento;
                  </Text>
                  <Text as="li" fontSize={'14px'} color={'#343A40'} lineHeight={'20px'}>
                    III. Pode revogar o consentimento a qualquer momento, de forma simples e gratuita, no{' '}
                    <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                      {optoutUrl ? <a href={optoutUrl} target="_blank" rel="noopener noreferrer">{optoutUrl}</a> : '[URL do Programa]'}
                    </Text>
                    .
                  </Text>
                </Box>
              </Box>
            </Checkbox>

            <Checkbox
              isChecked={professionalConsent}
              onChange={(e) => onProfessionalConsentChange(e.target.checked)}
              size="md"
              alignItems={'flex-start'}
            >
              <Text fontSize={'14px'} color={'#343A40'} ml={'8px'} lineHeight={'20px'} pt={0}>
                Autorizo o envio dos meus dados profissionais (nome e CRM) à indústria farmacêutica responsável,
                exclusivamente para confirmação de elegibilidade e contato operacional relacionados ao Programa. Estou
                ciente de que a Mevo não é responsável pelo gerenciamento do Programa, limitando-se a viabilizar o
                encaminhamento das informações autorizadas.
                {doctorConsentOptional && (
                  <Text as="span" color={'#6C757D'}>
                    {' '}(opcional)
                  </Text>
                )}
              </Text>
            </Checkbox>
          </Flex>
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'0px 20px 20px 20px'}>
        <Box padding={'16px'} backgroundColor={'#EEF1FF'} borderRadius={'10px'}>
          <Flex flexDirection={'row'} gap={'12px'}>
            <Box flexShrink={0}>
              <InfoIcon width={'16px'} height={'16px'} fill="#5C2E8C" />
            </Box>
            <Flex flexDirection={'column'} gap={'4px'}>
              <Text fontSize={'14px'} color={'#343A40'}>
                O Programa de Suporte ao Paciente (PSP) oferece apoio a adesão e amplia o acesso aos medicamentos.
              </Text>
              <Text
                fontSize={'14px'}
                color={'#343A40'}
                fontWeight={700}
                textDecoration={'underline'}
                cursor={'pointer'}
              >
                <a
                  href={partnerProgramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'inherit', cursor: 'inherit' }}
                >
                  Entenda como funciona
                </a>
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onClose}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Cancelar
            </Text>
          </Button>
          <Button
            onClick={onConfirm}
            isDisabled={isConfirmDisabled}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            rightIcon={<RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" />}
          >
            Continuar
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default PspConsentStep;
